import { fetcher } from '@commenda/utils';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { CONNECTIONS_ROUTE, connectionsKeys } from './keys';
import { IConnectionCategory, IConnectionToolkitPage } from './types';

export interface ConnectionToolkitsParams {
  search?: string;
  category?: string;
  isEnabled?: boolean;
}

const getToolkits = async (
  params: ConnectionToolkitsParams & { cursor?: string }
): Promise<IConnectionToolkitPage> => {
  const queryParams = new URLSearchParams();
  if (params.search) queryParams.append('search', params.search);
  if (params.category) queryParams.append('category', params.category);
  if (params.cursor) queryParams.append('cursor', params.cursor);

  const query = queryParams.toString();
  const response = await fetcher(
    `${CONNECTIONS_ROUTE}/toolkits${query ? `?${query}` : ''}`,
    { method: 'GET' }
  );

  if (!response.ok) throw new Error('Failed to load the app directory');

  return (await response.json()) as IConnectionToolkitPage;
};

export const useConnectionToolkits = (params: ConnectionToolkitsParams) =>
  useInfiniteQuery({
    queryKey: [connectionsKeys.TOOLKITS, params.search, params.category],
    queryFn: ({ pageParam }) =>
      getToolkits({
        search: params.search,
        category: params.category,
        cursor: pageParam || undefined,
      }),
    initialPageParam: '',
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: params.isEnabled ?? true,
  });

const getCategories = async (): Promise<IConnectionCategory[]> => {
  const response = await fetcher(`${CONNECTIONS_ROUTE}/categories`, {
    method: 'GET',
  });

  if (!response.ok) throw new Error('Failed to load app categories');

  return (await response.json()) as IConnectionCategory[];
};

export const useConnectionCategories = () =>
  useQuery({
    queryKey: [connectionsKeys.CATEGORIES],
    queryFn: getCategories,
  });
